import { useState, useEffect, useCallback } from 'react';
import {
  doc,
  getDocs,
  onSnapshot,
  query,
  setDoc
} from 'firebase/firestore';
import {
  createNewDispatchItem,
  formatDateForFirestore,
  generateDispatchData,
  convertFirestoreToUI
} from '../utils/dispatchUtils';
import { propertyCollection } from '../utils/firestorePaths';

/**
 * Custom hook for managing dispatch data operations
 * @param {string} clientId - Owning client ID
 * @param {string} selectedProperty - Current property ID
 * @returns {Object} - Dispatch data and operations
 */
export const useDispatchData = (clientId, selectedProperty) => {
  const [loading, setLoading] = useState(true);
  const [employeeList, setEmployeeList] = useState([]);
  const [roomsList, setRoomsList] = useState([]);
  const [selectedEmployeeDispatch, setSelectedEmployeeDispatch] = useState([]);
  const [saving, setSaving] = useState(false);

  // Fetch employees and rooms
  useEffect(() => {
    if (!selectedProperty) {
      setLoading(false);
      return;
    }

    const employeesRef = propertyCollection(clientId, selectedProperty, "employees");
    const roomsRef = propertyCollection(clientId, selectedProperty, "units");

    const unsubscribeEmployees = onSnapshot(query(employeesRef), (snapshot) => {
      const employees = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setEmployeeList(employees);
    });

    const unsubscribeRooms = onSnapshot(query(roomsRef), (snapshot) => {
      const rooms = snapshot.docs.map((doc) => doc.data());
      setRoomsList(rooms);
    });

    setLoading(false);

    return () => {
      unsubscribeEmployees();
      unsubscribeRooms();
    };
  }, [clientId, selectedProperty]);

  // Fetch existing dispatches for selected employee and date
  const fetchEmployeeDispatches = useCallback(async (employeeId, selectedDate, defaultStartTime = null) => {
    if (!employeeId || !selectedDate || !selectedProperty) {
      setSelectedEmployeeDispatch([]);
      return;
    }

    try {
      const formatDate = formatDateForFirestore(selectedDate);

      // Find the employee to check if they have a userId
      const employee = employeeList.find(emp => emp.id === employeeId);
      const dispatchDocId = employee?.userId || employeeId;

      const dispatchRef = propertyCollection(
        clientId,
        selectedProperty,
        "dispatches",
        formatDate,
        dispatchDocId
      );

      const docs = await getDocs(dispatchRef);

      if (!docs.empty) {
        const rows = docs.docs.map((doc) =>
          convertFirestoreToUI(doc.data(), doc.id)
        );
        setSelectedEmployeeDispatch(rows);
      } else {
        // Initialize with empty dispatch item, seeding the start time with the
        // employee's scheduled shift start for the day (if provided).
        const newItem = createNewDispatchItem();
        if (defaultStartTime) {
          newItem.startTime = defaultStartTime;
        }
        setSelectedEmployeeDispatch([newItem]);
      }
    } catch (error) {
      console.error("Error fetching employee dispatches:", error);
      setSelectedEmployeeDispatch([]);
    }
  }, [clientId, selectedProperty, employeeList]);

  // Add new dispatch row
  const addNewDispatchRow = useCallback(() => {
    setSelectedEmployeeDispatch(prev => {
      const newItem = createNewDispatchItem();

      // If there are existing items, set start time to last item's end time
      if (prev.length > 0) {
        const lastItem = prev[prev.length - 1];
        if (lastItem.endTime) {
          newItem.startTime = lastItem.endTime;
        }
      }

      return [...prev, newItem];
    });
  }, []);

  // Update dispatch item
  const updateDispatchItem = useCallback((id, field, value) => {
    setSelectedEmployeeDispatch(prev =>
      prev.map(row => row.id === id ? { ...row, [field]: value } : row)
    );
  }, []);

  // Remove dispatch item
  const removeDispatchItem = useCallback((id) => {
    setSelectedEmployeeDispatch(prev => prev.filter(row => row.id !== id));
  }, []);

  // Replace all dispatch items
  const replaceAllDispatchItems = useCallback((newItems) => {
    setSelectedEmployeeDispatch(newItems);
  }, []);

  // Save all dispatch items to Firestore
  const saveDispatchItems = useCallback(async (employeeId, selectedDate, itemsOverride = null) => {
    if (!employeeId || !selectedDate || !selectedProperty) {
      throw new Error("Missing required parameters for saving");
    }

    // Autosave passes an explicit (already-filtered) list so a tab/date change
    // mid-save can't retarget it; manual save falls back to the current board.
    const itemsToSave = itemsOverride || selectedEmployeeDispatch;

    setSaving(true);
    try {
      const formatDate = formatDateForFirestore(selectedDate);

      // Find the employee to check if they have a userId
      const employee = employeeList.find(emp => emp.id === employeeId);
      const dispatchDocId = employee?.userId || employeeId;

      const baseRef = propertyCollection(
        clientId,
        selectedProperty,
        "dispatches",
        formatDate,
        dispatchDocId
      );

      const savePromises = itemsToSave.map(async (item) => {
        const formattedData = generateDispatchData(item);
        const docRef = doc(baseRef, item.id.toString());
        return setDoc(docRef, formattedData, { merge: true });
      });

      await Promise.all(savePromises);
      return { success: true, message: "Tasks saved successfully" };
    } catch (error) {
      console.error("Error saving dispatch items:", error);
      return { success: false, message: "Failed to save tasks", error };
    } finally {
      setSaving(false);
    }
  }, [clientId, selectedProperty, selectedEmployeeDispatch, employeeList]);

  return {
    loading,
    saving,
    employeeList,
    roomsList,
    selectedEmployeeDispatch,
    fetchEmployeeDispatches,
    addNewDispatchRow,
    updateDispatchItem,
    removeDispatchItem,
    replaceAllDispatchItems,
    saveDispatchItems,
  };
};